import { supabase } from "@/integrations/supabase/client";

export interface CreditBalance {
  balance: number;
  lifetime_earned: number;
  lifetime_spent: number;
  plan: {
    id: string;
    name: string;
    monthly_credits: number;
    is_free: boolean;
  } | null;
}

export interface ActionCost {
  cost: number;
  display_name: string;
  description: string | null;
}

export interface CreditTransaction {
  id: string;
  action_type: string;
  credits_change: number;
  credits_before: number;
  credits_after: number;
  metadata: Record<string, unknown>;
  description: string | null;
  created_at: string;
}

export interface DeductResult {
  success: boolean;
  credits_used?: number;
  new_balance?: number;
  transaction_id?: string;
  error?: string;
  required?: number;
  available?: number;
}

export class CreditService {
  /**
   * Check the user's current credit balance
   */
  static async checkBalance(): Promise<CreditBalance> {
    const { data, error } = await supabase.functions.invoke('credit-service', {
      body: { action: 'check_balance' }
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);

    return {
      balance: data.balance,
      lifetime_earned: data.lifetime_earned,
      lifetime_spent: data.lifetime_spent,
      plan: data.plan
    };
  }

  /**
   * Get the credit cost for a specific action
   */
  static async getActionCost(actionType: string): Promise<ActionCost> {
    const { data, error } = await supabase.functions.invoke('credit-service', {
      body: { action: 'get_action_cost', action_type: actionType }
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);

    return {
      cost: data.cost,
      display_name: data.display_name,
      description: data.description
    };
  }

  /**
   * Deduct credits for an action
   * Returns success: false if insufficient credits
   */
  static async deductCredits(
    actionType: string,
    metadata?: Record<string, unknown>,
    description?: string
  ): Promise<DeductResult> {
    const { data, error } = await supabase.functions.invoke('credit-service', {
      body: { 
        action: 'deduct_credits', 
        action_type: actionType,
        metadata,
        description
      }
    });

    if (error) {
      // Check if it's an insufficient credits error
      if (data && !data.success && data.error === 'Insufficient credits') {
        return {
          success: false,
          error: data.error,
          required: data.required,
          available: data.available
        };
      }
      throw error;
    }

    return {
      success: data.success,
      credits_used: data.credits_used,
      new_balance: data.new_balance,
      transaction_id: data.transaction_id,
      error: data.error
    };
  }

  /**
   * Add credits to user account (admin or webhook use)
   */
  static async addCredits(
    amount: number,
    actionType?: string,
    metadata?: Record<string, unknown>,
    description?: string
  ): Promise<{ success: boolean; new_balance?: number; error?: string }> {
    const { data, error } = await supabase.functions.invoke('credit-service', {
      body: { 
        action: 'add_credits', 
        amount,
        action_type: actionType,
        metadata,
        description
      }
    });

    if (error) throw error;
    
    return {
      success: data.success,
      new_balance: data.new_balance,
      error: data.error
    };
  }

  /**
   * Get user's credit transaction history
   */
  static async getTransactions(limit = 50): Promise<CreditTransaction[]> {
    const { data, error } = await supabase.functions.invoke('credit-service', {
      body: { action: 'get_transactions', limit }
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);

    return data.transactions;
  }

  /**
   * Check if user has enough credits for an action
   */
  static async hasEnoughCredits(actionType: string): Promise<{ hasEnough: boolean; required: number; available: number }> {
    const [balance, cost] = await Promise.all([
      this.checkBalance(),
      this.getActionCost(actionType)
    ]);

    return {
      hasEnough: balance.balance >= cost.cost,
      required: cost.cost,
      available: balance.balance
    };
  }
}